import { formatDate } from '../../lib/format';
import type { CandidateDetail, CombinedScorecard } from '../../lib/types';
import {
  bandFor,
  bandRange,
  BANDS,
  breakdown,
  criticalConcerns,
  WEIGHTED_THRESHOLDS,
  weightedOutcome,
  WEIGHTS,
  type CompetencyInput,
  type RefScoreRow,
  type ScoreRow,
  type WeightedOutcome,
} from './scoring';

/**
 * Exports a saved scorecard as a PDF, via the browser's own print-to-PDF.
 *
 * Deliberately not a PDF library: jsPDF plus a table plugin is comparable in
 * size to this entire bundle, and the browser already renders and paginates
 * HTML better than we would hand-place boxes. The cost is that the user picks
 * "Save as PDF" in the print dialog rather than getting a direct download.
 * Same trade the account-management documents make, and the same reason CSV
 * beats a spreadsheet library in lib/download.ts.
 *
 * The report is built as a standalone document in an off-screen iframe rather
 * than by printing the page. Printing the page would mean fighting the app's
 * layout with print overrides, and the result would read as a screenshot of a
 * dialog. This is a document: it repeats table headers across pages, avoids
 * breaking a question away from its evidence, and prints the reasoning behind
 * the score rather than only the score.
 *
 * The colours are literal hex rather than `var(--brand-900)`, because the
 * iframe is a separate document and never sees tokens.css. They are quoted
 * from it, and the comment beside each is the token they came from.
 */

const NAVY = '#14395C'; /* --brand-900 */
const INK = '#2F4050'; /* --gray-700 */
const MUTED = '#546778'; /* --gray-500 */
const RULE = '#DCE3EA'; /* --gray-200 */
const WASH = '#F4F7FA'; /* --brand-50 */
const CRIMSON = '#AE1920'; /* --accent-600 */

const esc = (s: unknown): string =>
  String(s ?? '').replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c,
  );

const nl2br = (s: unknown): string => esc(s).replace(/\r?\n/g, '<br>');

/**
 * Rating ink. Banded with `>=` rather than `===` because competency scores are
 * weighted means — an `=== 3` test would paint a 3.9 as a concern and a 3.0 as
 * adequate. A 3 is navy: this palette has no green to reward "fine" with, so
 * "fine" is simply the absence of crimson.
 */
const ratingInk = (r: number): string => (r >= 3 ? NAVY : CRIMSON);

function styles(): string {
  return `
    @page { size: letter; margin: 15mm 14mm; }
    * { box-sizing: border-box; }
    body {
      margin: 0; color: ${INK}; background: #fff;
      font: 10.5pt/1.45 "Open Sans", "Segoe UI", Helvetica, Arial, sans-serif;
      -webkit-print-color-adjust: exact; print-color-adjust: exact;
    }
    h1, h2, th { font-family: Montserrat, "Open Sans", Helvetica, Arial, sans-serif; }
    h1 { font-size: 21pt; font-weight: 800; color: ${NAVY}; margin: 0; letter-spacing: -.2pt; }
    .sub { font-size: 12pt; color: ${MUTED}; margin: 2pt 0 0; }
    .rule { border-bottom: 2.5pt solid ${NAVY}; margin: 7pt 0 12pt; }
    h2 {
      font-size: 10pt; font-weight: 700; color: ${NAVY}; text-transform: uppercase;
      letter-spacing: .09em; margin: 15pt 0 6pt; padding-bottom: 3pt;
      border-bottom: 1pt solid ${RULE};
    }
    table { width: 100%; border-collapse: collapse; }
    th, td { text-align: left; padding: 5pt 7pt; vertical-align: top; }
    thead th {
      background: ${NAVY}; color: #fff; font-size: 8.5pt; font-weight: 700;
      text-transform: uppercase; letter-spacing: .05em;
    }
    tbody tr { border-bottom: .75pt solid ${RULE}; }
    .num { text-align: right; white-space: nowrap; }
    .meta { width: 100%; margin-bottom: 10pt; }
    .meta td { padding: 0 14pt 0 0; width: 25%; }
    .meta .k { font-size: 8pt; text-transform: uppercase; letter-spacing: .07em; color: ${MUTED}; }
    .meta .v { font-size: 10.5pt; color: ${NAVY}; font-weight: 600; }
    .verdict { border: 1pt solid; border-radius: 6pt; padding: 9pt 11pt; margin-bottom: 4pt; }
    .verdict .score { font-size: 17pt; font-weight: 800; font-family: Montserrat, sans-serif; }
    .verdict .why { font-size: 9.5pt; margin-top: 2pt; }
    .verdict .tally { font-size: 8.5pt; margin-top: 3pt; opacity: .8; }
    .warn {
      border: 1pt solid #F4B3B6; background: #FDEDED; border-radius: 6pt;
      padding: 8pt 11pt; margin-top: 8pt;
    }
    .warn h3 { font-size: 9.5pt; color: #8B1319; margin: 0 0 4pt; }
    .warn li { font-size: 9pt; color: #8B1319; margin-bottom: 2pt; }
    .band { font-size: 9.5pt; }
    .band.on { background: ${WASH}; font-weight: 600; }
    .band .lbl { color: ${NAVY}; }
    .band .desc { color: ${MUTED}; font-size: 8.5pt; }
    /* Ratings and evidence. Three columns so the question gets the width and
       the rating and weight stop taking a line each. */
    .qtable td { padding: 4pt 7pt; }
    .qtable .c-rate { width: 8%; text-align: center; }
    .qtable .c-weight { width: 16%; font-size: 8.5pt; color: ${MUTED}; white-space: nowrap; }
    .qtable .qt { font-size: 10pt; color: ${NAVY}; font-weight: 600; }
    /* The core value a question tests. On the row rather than in the heading,
       because it differs per question where the category does not. */
    .qtable .tag {
      display: inline-block; font-size: 7.5pt; font-weight: 700; color: ${MUTED};
      text-transform: uppercase; letter-spacing: .06em; border: .75pt solid ${RULE};
      border-radius: 3pt; padding: 0 3pt; margin-right: 5pt; vertical-align: 1.5pt;
    }
    .qtable .ev {
      font-size: 9.5pt; color: ${INK}; background: ${WASH}; border-left: 2pt solid ${RULE};
      padding: 4pt 8pt; margin-top: 3pt; border-radius: 0 4pt 4pt 0;
    }
    /* The competency, once per run of questions rather than on every row. */
    .qtable .grp td {
      font-size: 8.5pt; font-weight: 700; color: ${NAVY}; text-transform: uppercase;
      letter-spacing: .06em; background: ${WASH}; padding: 4pt 7pt;
    }
    .rate {
      display: inline-block; min-width: 15pt; text-align: center; font-weight: 800;
      font-family: Montserrat, sans-serif; font-size: 11pt;
    }
    .notes { background: ${WASH}; border-radius: 6pt; padding: 8pt 11pt; font-size: 9.5pt; }
    .foot { margin-top: 16pt; padding-top: 6pt; border-top: 1pt solid ${RULE}; font-size: 8pt; color: ${MUTED}; }

    /* THESE SIX WERE BEING USED AND HAD NEVER BEEN DEFINED. weightedBlock and
       the combined report emit .verdict-score, .verdict-label, .verdict-blurb,
       .concern, .n and .mono, and every one of them rendered as an unstyled
       block: the weighted score printed at body size, the hard-gate warning
       had no box, and every numeric column sat left-aligned in a proportional
       face so the figures did not line up. Only roles WITH a competency model
       reach that code, which is why it survived — the local test role has
       none, so the section never rendered. */
    /* Flex on the WEIGHTED variant only. The .verdict class is shared:
       weightedBlock wants the big number beside its label, verdictBlock stacks
       score, reason and tally. Putting display:flex on the shared class laid
       the stacked one out in a row. */
    .verdict.weighted { display: flex; gap: 11pt; align-items: baseline; }
    .verdict-score {
      font-family: Montserrat, sans-serif; font-size: 21pt; font-weight: 800;
      line-height: 1; min-width: 46pt;
    }
    .verdict-label { font-size: 11pt; font-weight: 700; color: ${NAVY}; }
    .verdict-blurb { font-size: 9.5pt; color: ${MUTED}; margin-top: 1pt; }
    .concern {
      border: 1pt solid #F4B3B6; background: #FDEDED; border-radius: 6pt;
      padding: 7pt 10pt; margin: 8pt 0 0; font-size: 9.5pt; color: #8B1319;
    }
    .n { text-align: right; white-space: nowrap; }
    .mono { font-family: "IBM Plex Mono", Consolas, monospace; font-size: 9pt; }

    /* Keep a question with its evidence, and a table row intact. */
    tr, .verdict, .warn, .concern { break-inside: avoid; page-break-inside: avoid; }
    thead { display: table-header-group; }
    /* A heading, and a competency subheading, must not be the last thing on a
       page with its rows overleaf. */
    h2, .qtable .grp { break-after: avoid; page-break-after: avoid; }
  `;
}

function verdictBlock(c: CandidateDetail, band: (typeof BANDS)[number]): string {
  return `
    <div class="verdict" style="border-color:${band.line};background:${band.wash};color:${band.ink}">
      <div class="score">${c.ScorePct}% &middot; ${esc(band.label)}</div>
      <div class="why">${esc(band.blurb)}</div>
      <div class="tally">
        ${c.ScoreTotal} of ${c.ScoreMax} weighted points &middot;
        ${c.RatedCount} of ${c.QuestionCount} questions rated
      </div>
    </div>`;
}

function concernsBlock(rows: ScoreRow[]): string {
  const low = criticalConcerns(rows);
  if (low.length === 0) return '';
  return `
    <div class="warn">
      <h3>${low.length} Critical question${low.length !== 1 ? 's' : ''} scored below 3</h3>
      <ul style="margin:0;padding-left:14pt">
        ${low.map((r) => `<li><strong>${r.rating}</strong> &nbsp;${esc(r.text)}</li>`).join('')}
      </ul>
    </div>`;
}

function breakdownTable(rows: ScoreRow[]): string {
  const cats = breakdown(rows);
  if (cats.length === 0) return '';
  return `
    <h2>Weighted breakdown by competency</h2>
    <table>
      <thead><tr>
        <th>Competency</th><th class="num">Weight</th><th class="num">Score</th><th class="num">Weighted</th>
      </tr></thead>
      <tbody>
        ${cats
          .map(
            (c) => `
          <tr>
            <td>${esc(c.category)} <span style="color:${MUTED}">&middot; ${c.n} rated</span></td>
            <td class="num" style="color:${MUTED}">${c.share}%</td>
            <td class="num" style="font-weight:800;color:${ratingInk(c.score)}">${c.score.toFixed(1)}</td>
            <td class="num" style="color:${MUTED}">${c.got} / ${c.max}</td>
          </tr>`,
          )
          .join('')}
      </tbody>
    </table>
    <p style="font-size:8.5pt;color:${MUTED};margin:5pt 0 0">
      Weight is the competency's share of the points on offer. Score is the weighted mean on the
      same 1&ndash;5 scale the interviewer rated on.
    </p>`;
}

function thresholdsTable(pct: number): string {
  const current = bandFor(pct);
  return `
    <h2>Decision thresholds</h2>
    <table>
      <tbody>
        ${BANDS.map(
          (b, i) => `
          <tr class="band${b === current ? ' on' : ''}">
            <td style="width:22%;white-space:nowrap;color:${b === current ? NAVY : MUTED}">${bandRange(i)}</td>
            <td>
              <span class="lbl" style="${b === current ? '' : `color:${MUTED}`}">${esc(b.label)}</span>
              <div class="desc">${esc(b.blurb)}</div>
            </td>
            <td class="num" style="width:14%;color:${NAVY}">${b === current ? `&larr; ${pct}%` : ''}</td>
          </tr>`,
        ).join('')}
      </tbody>
    </table>`;
}

/**
 * Ratings and evidence, grouped by competency.
 *
 * REWRITTEN BECAUSE THE OLD SHAPE SPRAWLED. Every question used to be a stack
 * of three full-width lines — an eyebrow repeating the competency and weight,
 * the question, and a line reading "No evidence recorded." when nobody had
 * typed anything. Measured at letter width that came to 84-104pt per question:
 * twenty-three questions filled two and a half pages, most of it white, with
 * the competency name repeated verbatim on every consecutive row and the right
 * half of each line empty.
 *
 * Three changes, each removing a line rather than shrinking type:
 *
 *   - The competency is a subheading printed ONCE per group, not an eyebrow on
 *     every row. Consecutive questions share it; that is what made it noise.
 *   - Weight moves into its own narrow column, so it stops competing with the
 *     question for the same line.
 *   - The "No evidence recorded" line is gone. An unanswered question is now
 *     silent, which is also the honest rendering: absence of a note is not a
 *     finding worth a line of its own on every row. Where there IS evidence it
 *     gets more room, which is the half of the section anybody rereads.
 *
 * A TABLE, NOT A GRID, because this is paged media. `thead` repeats across
 * pages and `tr` is the unit browsers already know not to break, so a question
 * and its evidence stay together without a wrapper that could strand a
 * subheading at the foot of a page.
 */
function questionList(scores: CandidateDetail['scores']): string {
  if (scores.length === 0) return '';

  /* Grouped in the order the questions were asked rather than sorted: the
     scorecard is a record of an interview, and re-ordering it would stop the
     paper copy matching the conversation it came from. */
  const groups: { label: string; rows: CandidateDetail['scores'] }[] = [];
  for (const s of scores) {
    /* Grouped by Category ALONE. The core value is not part of the heading,
       because Category is already "Core Values" for those rows and the value
       differs on every one — folding it in produced five headings above a
       single question each, which is the sprawl this was meant to remove. It
       goes on the row instead, where it labels the question it belongs to. */
    const label = s.Category ?? 'Uncategorised';
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.rows.push(s);
    else groups.push({ label, rows: [s] });
  }

  const body = groups
    .map(
      (g) => `
      <tr class="grp"><td colspan="3">${esc(g.label)}</td></tr>
      ${g.rows
        .map(
          (s) => `
        <tr>
          <td class="c-rate"><span class="rate" style="color:${ratingInk(s.Rating)}">${s.Rating}</span></td>
          <td>
            <div class="qt">${s.CoreValue ? `<span class="tag">${esc(s.CoreValue)}</span>` : ''}${esc(s.QuestionText)}</div>
            ${s.Comment ? `<div class="ev">${nl2br(s.Comment)}</div>` : ''}
          </td>
          <td class="c-weight">${esc(WEIGHTS[s.Weight]?.label ?? '')}</td>
        </tr>`,
        )
        .join('')}`,
    )
    .join('');

  return `
    <h2>Ratings and evidence</h2>
    <table class="qtable">
      <thead><tr>
        <th class="c-rate">Rating</th><th>Question and evidence</th><th class="c-weight">Weight</th>
      </tr></thead>
      <tbody>${body}</tbody>
    </table>`;
}

/**
 * The competency view, printed only when the role defines a model.
 *
 * A scorecard for such a role has to print the decision the way it was made —
 * weighted on the 1-5 scale with the hard gates checked — or the paper copy
 * taken into a debrief would say something different from the screen the
 * interviewer scored on. That is the failure this whole module exists to
 * avoid, which is why the arithmetic is imported rather than repeated.
 */
function weightedBlock(outcome: WeightedOutcome, stageName: string | null): string {
  const gates = outcome.gateFailures
    .map((g) => `${esc(g.ref)}. ${esc(g.name)}`)
    .join(', ');

  return `
  <h2>Recommendation${stageName ? ` &mdash; ${esc(stageName)}` : ''}</h2>
  <div class="verdict weighted" style="border-color:${outcome.gateFailures.length ? CRIMSON : RULE}">
    <div class="verdict-score" style="color:${
      outcome.gateFailures.length || (outcome.weighted ?? 0) < 3.5 ? CRIMSON : NAVY
    }">${outcome.weighted !== null ? outcome.weighted.toFixed(2) : '—'}</div>
    <div>
      <div class="verdict-label">${esc(outcome.decision.label)}</div>
      <div class="verdict-blurb">${esc(outcome.decision.blurb)}</div>
      <div class="verdict-blurb">Scored on ${outcome.covered}% of the competency model.</div>
    </div>
  </div>
  ${
    gates
      ? `<p class="concern"><strong>Hard gate failed.</strong> ${gates} scored below 3. These are the job &mdash; a strong average elsewhere does not offset them.</p>`
      : ''
  }
  <table>
    <thead><tr><th style="width:6%"></th><th>Competency</th><th class="n" style="width:12%">Weight</th><th class="n" style="width:10%">Rated</th><th class="n" style="width:12%">Score</th></tr></thead>
    <tbody>
      ${outcome.competencies
        .map(
          (k) => `<tr>
            <td class="mono">${esc(k.ref)}</td>
            <td>${esc(k.name)}${k.isGate ? ' <strong>(gate)</strong>' : ''}</td>
            <td class="n mono">${k.weightPct}%</td>
            <td class="n mono">${k.n || '—'}</td>
            <td class="n mono" style="color:${k.score === null ? MUTED : ratingInk(k.score)}">${
              k.score === null ? 'not scored' : k.score.toFixed(1)
            }</td>
          </tr>`,
        )
        .join('')}
    </tbody>
  </table>
  <table>
    <thead><tr><th style="width:38%">Weighted outcome</th><th>Decision</th></tr></thead>
    <tbody>
      ${WEIGHTED_THRESHOLDS.map(
        (t) => `<tr><td><strong>${esc(t.when)}</strong></td><td>${esc(t.then)}</td></tr>`,
      ).join('')}
    </tbody>
  </table>`;
}

export function buildScorecardHtml(
  c: CandidateDetail,
  competencies: CompetencyInput[] = [],
): string {
  const band = bandFor(c.ScorePct);
  const rows: ScoreRow[] = c.scores.map((s) => ({
    category: s.Category ?? 'Uncategorised',
    weight: s.Weight,
    rating: s.Rating,
    text: s.QuestionText,
  }));

  /* Only where the answers actually carry a competency: a scorecard saved
     before migration 089, or against a role with no model, has none and falls
     back to the percentage band below. */
  const refRows: RefScoreRow[] = c.scores.map((s) => ({
    ref: s.CompetencyRef,
    weight: s.Weight,
    rating: s.Rating,
  }));
  const outcome =
    competencies.length > 0 && refRows.some((r) => r.ref) ? weightedOutcome(refRows, competencies) : null;

  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8">
<title>Interview scorecard — ${esc(c.Name)}</title>
<style>${styles()}</style>
</head><body>
  <h1>Interview Scorecard</h1>
  <p class="sub">${esc(c.Name)} &middot; ${esc(c.RoleName ?? '')}</p>
  <div class="rule"></div>

  <table class="meta"><tr>
    <td><div class="k">Interviewer</div><div class="v">${esc(c.Interviewer || '—')}</div></td>
    <td><div class="k">Interviewed</div><div class="v">${esc(formatDate(c.InterviewedOn ?? c.CreatedUtc))}</div></td>
    <td><div class="k">Scorecard saved</div><div class="v">${esc(formatDate(c.CreatedUtc))}</div></td>
    <td><div class="k">Questions rated</div><div class="v">${c.RatedCount} of ${c.QuestionCount}</div></td>
  </tr></table>

  ${outcome ? weightedBlock(outcome, c.StageName) : verdictBlock(c, band)}
  ${concernsBlock(rows)}
  ${c.Notes ? `<h2>Interviewer notes</h2><div class="notes">${nl2br(c.Notes)}</div>` : ''}
  ${outcome ? '' : breakdownTable(rows)}
  ${outcome ? '' : thresholdsTable(c.ScorePct)}
  ${questionList(c.scores)}

  <div class="foot">
    Questions, weights and evidence are stored with the scorecard, so this report reflects the
    bank as it stood at the interview.
  </div>
</body></html>`;
}

/**
 * Prints from a detached iframe.
 *
 * The iframe stays in the DOM until printing finishes — removing it
 * synchronously after `print()` cancels the job in some browsers, which reads
 * to the user as "the button does nothing".
 */
export function exportScorecardPdf(
  candidate: CandidateDetail,
  competencies: CompetencyInput[] = [],
): void {
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden';
  document.body.appendChild(frame);

  const done = () => {
    if (frame.parentNode) frame.remove();
  };

  frame.onload = () => {
    const win = frame.contentWindow;
    if (!win) {
      done();
      return;
    }
    try {
      win.focus();
      /* Fires after the dialog closes, whether the user saved or cancelled. */
      win.onafterprint = done;
      win.print();
      /* Safari never fires onafterprint; clean up on a delay it cannot outlive. */
      setTimeout(done, 60_000);
    } catch {
      done();
    }
  };

  frame.srcdoc = buildScorecardHtml(candidate, competencies);
}

/* =========================================================================
   The combined scorecard — every stage, one decision
   ========================================================================= */

/**
 * A staged process produces several scorecards for one person: a screen, a
 * deep dive, four panellists, an executive final. Each covers a slice of the
 * competency model and none of them is the decision.
 *
 * This is the document the debrief is run from. It pools every rated answer,
 * weights it exactly as the live screen does, and states one recommendation.
 *
 * IT SHOWS WHERE THE PANEL DISAGREED. Two interviewers can score the same
 * competency 2 and 5, and a mean of 3.5 hides precisely the conversation that
 * needs to happen — the matrix says disagreements are resolved by evidence,
 * which is impossible if the paperwork has already averaged them away. Any
 * competency with a spread of two points or more is called out with the range.
 *
 * The contributing scorecards are listed with what each covered, so a
 * recommendation drawn from two stages out of five cannot be mistaken for a
 * complete assessment.
 */
export function buildCombinedHtml(
  combined: CombinedScorecard,
  roleName: string,
  competencies: CompetencyInput[],
): string {
  const rows: RefScoreRow[] = combined.answers.map((a) => ({
    ref: a.CompetencyRef,
    weight: a.Weight,
    rating: a.Rating,
  }));
  const outcome = weightedOutcome(rows, competencies);

  /* Per competency, the lowest and highest rating anyone gave it. Computed
     from the raw answers rather than from the means, because the disagreement
     is between people and the mean is what buried it. */
  const spread = new Map<string, { lo: number; hi: number }>();
  for (const a of combined.answers) {
    if (!a.CompetencyRef) continue;
    const cur = spread.get(a.CompetencyRef);
    spread.set(a.CompetencyRef, {
      lo: Math.min(cur?.lo ?? a.Rating, a.Rating),
      hi: Math.max(cur?.hi ?? a.Rating, a.Rating),
    });
  }
  const contested = outcome.competencies
    .filter((c) => {
      const sp = spread.get(c.ref);
      return sp && sp.hi - sp.lo >= 2;
    })
    .map((c) => ({ c, sp: spread.get(c.ref)! }));

  const cardRows = combined.cards
    .map(
      (c) => `<tr>
        <td>${esc(c.StageName ?? 'Unstaged')}</td>
        <td>${esc(c.Interviewer || '—')}</td>
        <td>${esc(formatDate(c.InterviewedOn ?? c.CreatedUtc))}</td>
        <td class="n mono">${c.RatedCount}</td>
      </tr>`,
    )
    .join('');

  const evidence = combined.answers
    .filter((a) => (a.Comment ?? '').trim())
    .map(
      (a) => `<tr>
        <td class="mono">${esc(a.CompetencyRef ?? '—')}</td>
        <td>${esc(a.QuestionText)}</td>
        <td class="n mono" style="color:${ratingInk(a.Rating)}">${a.Rating}</td>
        <td>${nl2br(a.Comment)}</td>
      </tr>`,
    )
    .join('');

  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8">
<title>Combined scorecard — ${esc(combined.name)}</title>
<style>${styles()}</style>
</head><body>
  <h1>Combined Scorecard</h1>
  <p class="sub">${esc(combined.name)} &middot; ${esc(roleName)}</p>
  <div class="rule"></div>

  <table class="meta"><tr>
    <td><div class="k">Scorecards</div><div class="v">${combined.cards.length}</div></td>
    <td><div class="k">Answers pooled</div><div class="v">${combined.answers.length}</div></td>
    <td><div class="k">Model covered</div><div class="v">${outcome.covered}%</div></td>
    <td><div class="k">Prepared</div><div class="v">${esc(formatDate(new Date().toISOString()))}</div></td>
  </tr></table>

  ${weightedBlock(outcome, null)}

  ${
    contested.length
      ? `<h2>Where the panel disagreed</h2>
  <p class="concern">These competencies were scored at least two points apart by different
  interviewers. The mean below hides that; resolve it with evidence rather than by seniority.</p>
  <table>
    <thead><tr><th style="width:6%"></th><th>Competency</th><th class="n" style="width:22%">Range given</th><th class="n" style="width:14%">Mean</th></tr></thead>
    <tbody>${contested
      .map(
        ({ c, sp }) => `<tr>
          <td class="mono">${esc(c.ref)}</td>
          <td>${esc(c.name)}</td>
          <td class="n mono" style="color:${CRIMSON}">${sp.lo} &ndash; ${sp.hi}</td>
          <td class="n mono">${c.score !== null ? c.score.toFixed(1) : '—'}</td>
        </tr>`,
      )
      .join('')}</tbody>
  </table>`
      : ''
  }

  <h2>What this is made of</h2>
  <table>
    <thead><tr><th>Stage</th><th>Interviewer</th><th>Interviewed</th><th class="n" style="width:14%">Rated</th></tr></thead>
    <tbody>${cardRows}</tbody>
  </table>

  ${
    evidence
      ? `<h2>Evidence</h2>
  <table>
    <thead><tr><th style="width:6%"></th><th style="width:40%">Question</th><th class="n" style="width:8%">Score</th><th>What was said</th></tr></thead>
    <tbody>${evidence}</tbody>
  </table>`
      : ''
  }
</body></html>`;
}

/** Render and hand to the print dialog. Same harness as every other export. */
export function exportCombinedPdf(
  combined: CombinedScorecard,
  roleName: string,
  competencies: CompetencyInput[],
): void {
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden';
  document.body.appendChild(frame);

  const done = () => {
    if (frame.parentNode) frame.remove();
  };

  frame.onload = () => {
    const win = frame.contentWindow;
    if (!win) {
      done();
      return;
    }
    try {
      win.focus();
      win.onafterprint = done;
      win.print();
      setTimeout(done, 60_000);
    } catch {
      done();
    }
  };

  frame.srcdoc = buildCombinedHtml(combined, roleName, competencies);
}
